import EyeOpenStrikethroughIcon from '@atlaskit/icon/core/eye-open-strikethrough';
import TableCellClearIcon from '@atlaskit/icon/core/table-cell-clear';
import TableColumnDeleteIcon from '@atlaskit/icon/core/table-column-delete';
import DuplicateIcon from '@atlaskit/icon-lab/core/duplicate';
import { Box, Inline } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { SHORTCUTS } from '@databases/keyboard-shortcuts';
import React from 'react';
import { useIntl } from 'react-intl-next';

import { ActionComponent } from '../ActionComponent.tsx';
import { type SelectFieldActionsAPI } from '../actions/fields/select-fields-actions.ts';
import { messages } from '../messages.ts';

import { MoveFields } from './MoveFields.tsx';

export type SelectFieldsActionsProps = {
  actions: SelectFieldActionsAPI;
  canMoveLeft: boolean;
  canMoveRight: boolean;
  handleDeleteFields: () => void;
  isDeleteDisabled: boolean;
  shouldShowClearCells: boolean;
};
export const SelectFieldsActions = ({
  actions,
  canMoveLeft,
  canMoveRight,
  handleDeleteFields,
  isDeleteDisabled,
  shouldShowClearCells
}: SelectFieldsActionsProps) => {
  const { formatMessage } = useIntl();

  const {
    handleDuplicateFields,
    handleClearFields,
    handleMoveFieldsLeft,
    handleMoveFieldsRight,
    handleHideFields
  } = actions;

  const canMove = canMoveLeft || canMoveRight;

  return (
    <Inline
      testId="fields-actions-container"
      spread="space-between"
      alignBlock="center"
      space="space.150"
    >
      <ActionComponent
        type="duplicate"
        selectionMode="field"
        message={formatMessage(messages['action-bar.field-duplicate-action'])}
        iconBefore={
          <DuplicateIcon
            label={formatMessage(messages['action-bar.field-duplicate-action'])}
          />
        }
        onClick={handleDuplicateFields}
        shortcut={SHORTCUTS.DUPLICATE}
      />
      {canMove && (
        <MoveFields
          handleMoveFieldsLeft={handleMoveFieldsLeft}
          handleMoveFieldsRight={handleMoveFieldsRight}
          canMoveLeft={canMoveLeft}
          canMoveRight={canMoveRight}
        />
      )}
      <ActionComponent
        type="hide"
        selectionMode="field"
        message={formatMessage(messages['action-bar.field-hide-action'])}
        iconBefore={
          <EyeOpenStrikethroughIcon
            label={formatMessage(messages['action-bar.field-hide-action'])}
          />
        }
        onClick={handleHideFields}
      />
      {shouldShowClearCells && (
        <ActionComponent
          type="clear-cells"
          selectionMode="field"
          message={formatMessage(messages['action-bar.clear-values-action'])}
          iconBefore={
            <TableCellClearIcon
              label={formatMessage(messages['action-bar.clear-values-action'])}
            />
          }
          onClick={handleClearFields}
          shortcut={SHORTCUTS.CLEAR}
        />
      )}
      {!isDeleteDisabled && (
        <ActionComponent
          type="delete-field"
          selectionMode="field"
          message={formatMessage(messages['action-bar.field-delete-action'])}
          iconBefore={
            <Box
              style={{
                color: token('color.background.danger.pressed')
              }}
            >
              <TableColumnDeleteIcon
                label={formatMessage(messages['action-bar.field-delete-action'])}
              />
            </Box>
          }
          onClick={handleDeleteFields}
          shortcut={SHORTCUTS.DELETE}
        />
      )}
    </Inline>
  );
};
